import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import {
  CustomSelect,
  CustomTextField,
  CustomCheckbox,
  CustomButton,
  HeaderFilterBox,
} from '../../../common';
import { generateYearMenuItems } from '../utils/couponsMailsUtils';
import { QUARTER_OPTIONS, MONTH_OPTIONS } from '../constants/couponsMailsConstants';

const CouponsMailsFilters = ({
  filter,
  handleFilter,
  includeAllSubscribers,
  onIncludeAllSubscribersChange,
  onSearch,
  plans,
  groups,
  totalCount = 0,
  selectedCount = 0,
  bulkTotalCount = 0,
  isAllSubscribersLoading = false,
}) => {
  const controlSx = {
    mt: 0,
    mb: 0,
    minWidth: 0,
  };

  const handleTimePeriodChange = (_, value) => {
    if (!value || value === filter.timePeriod) return;

    handleFilter({
      target: {
        name: 'timePeriod',
        value,
        type: 'select',
      },
    });
  };

  return (
    <HeaderFilterBox
      variant="twoRows"
      sx={{
        px: { xs: 2, md: 3 },
        py: 2.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Filtros de envío
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`Registros: ${totalCount}`} />
            <Chip
              size="small"
              color={selectedCount > 0 ? 'primary' : 'default'}
              variant={selectedCount > 0 ? 'filled' : 'outlined'}
              label={`Seleccionados: ${selectedCount}`}
            />
            {includeAllSubscribers && (
              <Chip
                size="small"
                color="primary"
                icon={
                  isAllSubscribersLoading ? <CircularProgress size={14} /> : <FactCheckIcon />
                }
                label={
                  isAllSubscribersLoading
                    ? 'Preparando todos'
                    : `Enviar a todos: ${bulkTotalCount}`
                }
              />
            )}
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(12, minmax(0, 1fr))',
            },
            gap: 2,
            alignItems: 'end',
          }}
        >
          <CustomSelect
            label="Plan"
            name="plan"
            value={filter.plan}
            onChange={handleFilter}
            sx={{ ...controlSx, gridColumn: { lg: 'span 3' } }}
            size="medium"
          >
            {plans.map((plan) => (
              <MenuItem key={plan.uuid} value={plan.uuid}>
                {plan.name}
              </MenuItem>
            ))}
          </CustomSelect>

          <CustomSelect
            label="Grupo"
            name="group"
            value={filter.group}
            onChange={handleFilter}
            sx={{ ...controlSx, gridColumn: { lg: 'span 3' } }}
            size="medium"
            displayEmpty
          >
            <MenuItem value="">Todos</MenuItem>
            {groups.map((group) => (
              <MenuItem key={group.uuid} value={group.uuid}>
                {group.name}
              </MenuItem>
            ))}
          </CustomSelect>

          <CustomTextField
            label="N° Contrato"
            name="order"
            value={filter.order}
            onChange={handleFilter}
            sx={{ ...controlSx, gridColumn: { lg: 'span 2' } }}
            size="medium"
          />

          <CustomTextField
            label="Cuota pura"
            name="cuotaPura"
            value={filter.cuotaPura}
            onChange={handleFilter}
            disabled={filter.notCancelled}
            sx={{ ...controlSx, gridColumn: { lg: 'span 2' } }}
            size="medium"
          />

          <CustomButton
            variant="base"
            buttonIcon={SearchIcon}
            onClick={onSearch}
            sx={{
              gridColumn: { xs: '1 / -1', sm: 'span 2', lg: 'span 2' },
              width: '100%',
            }}
            size="medium"
          >
            Buscar
          </CustomButton>

          <Box
            sx={{
              gridColumn: { xs: '1 / -1', lg: 'span 3' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <CalendarMonthIcon color="primary" fontSize="small" />
            <ToggleButtonGroup
              exclusive
              value={filter.timePeriod}
              onChange={handleTimePeriodChange}
              size="medium"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  flex: 1,
                  minHeight: 56,
                  textTransform: 'none',
                  fontWeight: 700,
                  letterSpacing: 0,
                },
              }}
            >
              <ToggleButton value="month">Mes</ToggleButton>
              <ToggleButton value="quarter">Trimestre</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {filter.timePeriod === 'month' && (
            <CustomSelect
              label="Mes"
              name="month"
              value={filter.month}
              onChange={handleFilter}
              sx={{ ...controlSx, gridColumn: { lg: 'span 3' } }}
              size="medium"
            >
              {MONTH_OPTIONS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </CustomSelect>
          )}

          {filter.timePeriod === 'quarter' && (
            <CustomSelect
              label="Trimestre"
              name="quarter"
              value={filter.quarter}
              onChange={handleFilter}
              sx={{ ...controlSx, gridColumn: { lg: 'span 3' } }}
              size="medium"
            >
              {QUARTER_OPTIONS.map((quarter) => (
                <MenuItem key={quarter.value} value={quarter.value}>
                  {quarter.label}
                </MenuItem>
              ))}
            </CustomSelect>
          )}

          <CustomSelect
            label="Año"
            name="year"
            value={filter.year}
            onChange={handleFilter}
            sx={{ ...controlSx, gridColumn: { lg: 'span 2' } }}
            size="medium"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: '300px',
                  overflow: 'auto',
                },
              },
            }}
          >
            {generateYearMenuItems()}
          </CustomSelect>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0, sm: 2 }}
            sx={{
              gridColumn: { xs: '1 / -1', lg: 'span 4' },
              minHeight: 56,
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: 'wrap',
            }}
          >
            <CustomCheckbox
              checked={filter.notCancelled}
              onChange={handleFilter}
              name="notCancelled"
              label="No cancelados"
              sx={{ minHeight: 56 }}
            />

            <CustomCheckbox
              checked={filter.mailCouponEnabled}
              onChange={handleFilter}
              name="mailCouponEnabled"
              label="Habilitado cupón por mail"
              sx={{ minHeight: 56 }}
            />

            <CustomCheckbox
              checked={includeAllSubscribers}
              onChange={onIncludeAllSubscribersChange}
              name="includeAllSubscribers"
              label="Enviar a todos"
              sx={{ minHeight: 56 }}
            />
          </Stack>
        </Box>

        <Divider />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            color={filter.notCancelled ? 'success' : 'warning'}
            variant="outlined"
            label={filter.notCancelled ? 'Cupones no cancelados' : 'Modo deuda'}
          />
          {filter.mailCouponEnabled && (
            <Chip size="small" color="success" variant="outlined" label="Cupón por mail habilitado" />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={
              filter.timePeriod === 'month'
                ? `Periodo mensual ${filter.month}/${filter.year}`
                : `Trimestre ${filter.quarter || 'todos'} / ${filter.year}`
            }
          />
          {filter.order && (
            <Chip size="small" variant="outlined" label={`Contrato ${filter.order}`} />
          )}
        </Stack>
      </Stack>
    </HeaderFilterBox>
  );
};

export default CouponsMailsFilters;
